import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  PieChart,
  Activity,
  Users,
  FileText,
  CreditCard,
  Bell,
  Calendar,
  CheckCircle,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface HeroSectionProps {
  onGetStartedClick?: () => void;
}

const HeroSection = ({ onGetStartedClick = () => {} }: HeroSectionProps) => {
  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-yellow-100 via-yellow-50 to-yellow-100 py-20 md:py-28">
      <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between">
        <div className="md:w-1/2 text-left mb-12 md:mb-0 md:pr-8">
          <motion.h1
            className="text-4xl md:text-6xl font-bold tracking-tight text-gray-900 mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            Launch your UAE business
            <span className="block mt-2 text-coral-600">today</span>
          </motion.h1>

          <motion.div
            className="text-xl text-gray-600 mb-8 flex items-center"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="relative h-8 overflow-hidden">
              <motion.div
                className="absolute"
                animate={{ y: [0, -32, -64, -96, 0] }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  times: [0, 0.25, 0.5, 0.75, 1],
                }}
              >
                <div className="h-8 flex items-center font-semibold text-coral-600">
                  Formations
                </div>
                <div className="h-8 flex items-center font-semibold text-coral-600">
                  Accounting & taxes
                </div>
                <div className="h-8 flex items-center font-semibold text-coral-600">
                  Bank account opening
                </div>
                <div className="h-8 flex items-center font-semibold text-coral-600">
                  Pro & Visas
                </div>
              </motion.div>
            </div>
            <span className="ml-2">in one place</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex space-x-4"
          >
            <Button
              onClick={onGetStartedClick}
              className="bg-coral-600 hover:bg-coral-700 text-white px-8 py-6 rounded-xl text-lg font-medium shadow-lg"
            >
              Get a call back
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 ml-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="border-coral-600 text-coral-600 hover:bg-coral-50 px-8 py-6 rounded-xl text-lg font-medium"
            >
              Learn More
            </Button>
          </motion.div>
        </div>

        <div className="md:w-1/2 relative">
          <motion.div
            className="relative z-10 mx-auto max-w-md"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="relative">
              {/* Ownership Chart - Floating Card */}
              <motion.div
                className="absolute -left-16 top-0 bg-white rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(255,127,80,0.15)] w-80"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-700">
                      Ownership
                    </h3>
                    <div className="bg-mint-500 text-white p-2 rounded-xl">
                      <span className="text-sm font-bold">UAE</span>
                    </div>
                  </div>

                  <div className="flex justify-center mb-4">
                    <div className="relative w-32 h-32">
                      {/* Circular Progress Chart */}
                      <svg className="w-full h-full" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#f3f4f6"
                          strokeWidth="10"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#4ade80"
                          strokeWidth="10"
                          strokeDasharray="251.2"
                          strokeDashoffset="125.6"
                          strokeLinecap="round"
                          transform="rotate(-90 50 50)"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-2xl font-bold">50%</div>
                          <div className="text-xs text-gray-500">
                            Local Partner
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 rounded-full bg-skyblue-200 flex items-center justify-center text-xs font-medium border-2 border-white">
                        AM
                      </div>
                      <div className="w-8 h-8 rounded-full bg-mint-200 flex items-center justify-center text-xs font-medium border-2 border-white">
                        JK
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Setup Progress - Floating Card */}
              <motion.div
                className="absolute right-0 top-20 bg-white rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(186,156,252,0.15)] w-96"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-700">
                      Setup Progress
                    </h3>
                    <div className="bg-green-50 px-3 py-1 rounded-full text-green-600 text-xs font-medium">
                      2/4 Steps
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="bg-mint-100 p-2 rounded-full mr-3">
                        <CheckCircle className="h-4 w-4 text-mint-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm">Name</span>
                          <span className="text-xs text-mint-600">
                            Completed
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="bg-mint-100 p-2 rounded-full mr-3">
                        <CheckCircle className="h-4 w-4 text-mint-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm">Details</span>
                          <span className="text-xs text-mint-600">
                            Completed
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="bg-sunshine-100 p-2 rounded-full mr-3">
                        <div className="h-4 w-4 flex items-center justify-center text-sunshine-600 font-bold text-xs">
                          3
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm">Founders</span>
                          <span className="text-xs text-sunshine-600">
                            In Progress
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="bg-gray-100 p-2 rounded-full mr-3">
                        <div className="h-4 w-4 flex items-center justify-center text-gray-400 font-bold text-xs">
                          4
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm text-gray-400">
                            Documents
                          </span>
                          <span className="text-xs text-gray-500">Pending</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* License Status - Floating Badge */}
              <motion.div
                className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-2 border border-mint-200"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="h-3 w-3 bg-mint-500 rounded-full"></div>
                <span className="text-sm font-medium">License Active</span>
              </motion.div>

              {/* Support Chat - Floating Card */}
              <motion.div
                className="absolute -bottom-10 right-0 bg-white rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(79,209,197,0.15)] w-64"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <div className="p-5">
                  <div className="flex items-center mb-3">
                    <div className="bg-skyblue-100 p-2 rounded-full mr-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-skyblue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-medium">Need help?</p>
                      <p className="text-xs text-gray-500">
                        Our team is ready to assist
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <button className="bg-skyblue-600 text-white px-4 py-2 rounded-xl text-sm font-medium shadow-md">
                      Chat Now
                    </button>
                    <button className="bg-lavender-100 text-lavender-700 px-4 py-2 rounded-xl text-sm font-medium">
                      Call
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Main Dashboard Preview - Central Element */}
              <motion.div
                className="mx-auto w-full max-w-md h-auto bg-white rounded-3xl shadow-[0_20px_60px_rgba(59,130,246,0.2)] overflow-hidden"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-gradient-to-r from-coral-500 to-coral-600 p-4 text-white">
                  <div className="flex items-center">
                    <div className="bg-white text-coral-600 p-2 rounded-lg mr-3">
                      <span className="text-sm font-bold">UAE</span>
                    </div>
                    <h2 className="text-xl font-semibold">Business Portal</h2>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 mb-4 font-medium">
                    Your business dashboard is ready
                  </p>
                  <Progress
                    value={75}
                    className="w-full mb-4 bg-lavender-100"
                  />
                  <p className="text-sm text-gray-500 mb-6">
                    75% of your profile is complete
                  </p>

                  <div className="grid grid-cols-2 gap-6 mb-4">
                    <div className="bg-yellow-50 p-4 rounded-xl hover:shadow-md transition-shadow">
                      <FileText className="h-6 w-6 text-yellow-500 mb-3" />
                      <p className="text-sm font-medium">Fast Formation</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Establish LLC in minutes
                      </p>
                    </div>
                    <div className="bg-mint-50 p-4 rounded-xl hover:shadow-md transition-shadow">
                      <Users className="h-6 w-6 text-mint-500 mb-3" />
                      <p className="text-sm font-medium">EIN & ITIN</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Simple online application
                      </p>
                    </div>
                    <div className="bg-skyblue-50 p-4 rounded-xl hover:shadow-md transition-shadow">
                      <CreditCard className="h-6 w-6 text-skyblue-500 mb-3" />
                      <p className="text-sm font-medium">Business Banking</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Easy account setup
                      </p>
                    </div>
                    <div className="bg-lavender-50 p-4 rounded-xl hover:shadow-md transition-shadow">
                      <Calendar className="h-6 w-6 text-lavender-500 mb-3" />
                      <p className="text-sm font-medium">Tax Filing</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Automated calculations
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Ownership Chart - Floating Card */}
              <motion.div
                className="absolute -left-16 top-10 bg-white rounded-3xl overflow-hidden shadow-[0_20px_60px_rgba(255,127,80,0.15)] w-64"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-medium text-gray-700">
                      Ownership
                    </h3>
                    <div className="bg-mint-500 text-white p-1 rounded-lg">
                      <span className="text-xs font-bold">UAE</span>
                    </div>
                  </div>
                  <div className="flex justify-center mb-3">
                    <div className="relative w-24 h-24">
                      <svg className="w-full h-full" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#f3f4f6"
                          strokeWidth="10"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#4ade80"
                          strokeWidth="10"
                          strokeDasharray="251.2"
                          strokeDashoffset="125.6"
                          strokeLinecap="round"
                          transform="rotate(-90 50 50)"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-xl font-bold">50%</div>
                          <div className="text-xs text-gray-500">
                            Local Partner
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-300 rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-coral-200 rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute top-1/3 right-1/3 w-48 h-48 bg-mint-200 rounded-full filter blur-3xl opacity-10"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-skyblue-200 rounded-full filter blur-3xl opacity-10"></div>
      </div>
    </div>
  );
};

export default HeroSection;
